import React, { useState } from 'react';
import { Badge, Button, Card, Stack, Text, Tooltip } from '@alga-psa/ui-kit';
import type { ApplianceStatus, BadgeTone, CodeState, EntitlementKind, InstallCodeState } from './types';

// ── Formatting ───────────────────────────────────────────────────────────────

export function relTime(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  return relFromMs(then);
}

/** "3d ago" / "in 12d" — signed relative time from a millisecond timestamp. */
export function relFromMs(ms: number): string {
  const diff = ms - Date.now();
  const abs = Math.abs(diff);
  const mins = Math.floor(abs / 60000);
  const label = mins < 1 ? 'now'
    : mins < 60 ? `${mins}m`
    : mins < 1440 ? `${Math.floor(mins / 60)}h`
    : `${Math.floor(mins / 1440)}d`;
  if (mins < 1) return 'just now';
  return diff < 0 ? `${label} ago` : `in ${label}`;
}

export function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// Install-code expiry and token claims arrive as unix seconds, not ISO strings.
export function fmtUnixDate(sec: number | null): string {
  if (sec == null) return '—';
  const d = new Date(sec * 1000);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function fmtUnixDateTime(sec: number | null): string {
  if (sec == null) return '—';
  return fmtDateTime(new Date(sec * 1000).toISOString());
}

/** yyyy-mm-dd for <input type="date">, in local time. */
export function toDateInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** End of the chosen local day, as unix seconds. */
export function dateInputToUnix(value: string): number | null {
  if (!value) return null;
  const d = new Date(`${value}T23:59:59`);
  const ms = d.getTime();
  return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
}

// An absolute date with the relative time as a muted secondary (e.g. "Jul 15, 2026 · 3d ago").
export function DateText({ iso, withTime = false }: { iso: string | null; withTime?: boolean }) {
  if (!iso || Number.isNaN(new Date(iso).getTime())) return <Text size="sm">—</Text>;
  return (
    <Stack direction="row" gap={6} align="baseline" style={{ flexWrap: 'wrap' }}>
      <Text size="sm">{withTime ? fmtDateTime(iso) : fmtDate(iso)}</Text>
      <Text size="xs" tone="muted">{relTime(iso)}</Text>
    </Stack>
  );
}

export function UnixDateText({ sec, withTime = false }: { sec: number | null; withTime?: boolean }) {
  if (sec == null) return <Text size="sm">—</Text>;
  return <DateText iso={new Date(sec * 1000).toISOString()} withTime={withTime} />;
}

// ── Tones ────────────────────────────────────────────────────────────────────

export const statusTone: Record<ApplianceStatus, BadgeTone> = {
  registered: 'default',
  installed: 'info',
  active: 'success',
  suspended: 'warning',
  cancelled: 'danger',
};

export const codeTone: Record<InstallCodeState, BadgeTone> = {
  live: 'info',
  consumed: 'success',
  revoked: 'danger',
  expired: 'warning',
  none: 'default',
};

export const codeStateTone: Record<CodeState, BadgeTone> = {
  live: 'info',
  consumed: 'success',
  revoked: 'danger',
  expired: 'warning',
};

export const entitlementKindTone: Record<EntitlementKind, BadgeTone> = {
  stripe: 'success',
  comp: 'info',
};

export const toneColor: Record<BadgeTone, string> = {
  default: 'var(--alga-muted-fg, #64748b)',
  info: 'var(--alga-secondary, #40cff9)',
  success: 'var(--alga-success, #22c55e)',
  warning: 'var(--alga-warning, #f59e0b)',
  danger: 'var(--alga-danger, #ef4444)',
};

export type LicenseHealth = 'ok' | 'soon' | 'expired' | 'none';

/** License expiry health: green > 7 days, amber ≤ 7 days, red past, grey none. */
export function licenseHealth(exp: number | null): LicenseHealth {
  if (exp == null) return 'none';
  const days = (exp * 1000 - Date.now()) / 86400000;
  if (days < 0) return 'expired';
  return days <= 7 ? 'soon' : 'ok';
}

export const licenseHealthMeta: Record<LicenseHealth, { tone: BadgeTone; label: string }> = {
  ok: { tone: 'success', label: 'Valid' },
  soon: { tone: 'warning', label: 'Expires ≤7d' },
  expired: { tone: 'danger', label: 'Expired' },
  none: { tone: 'default', label: 'None' },
};

export function LicenseExpBadge({ exp }: { exp: number | null }) {
  const h = licenseHealth(exp);
  if (h === 'none') return <Badge tone="default">none</Badge>;
  const badge = <Badge tone={licenseHealthMeta[h].tone}>{h === 'expired' ? `expired ${relFromMs(exp! * 1000)}` : relFromMs(exp! * 1000)}</Badge>;
  return <Tooltip content={fmtUnixDateTime(exp)}><span style={{ display: 'inline-flex' }}>{badge}</span></Tooltip>;
}

// ── Shared presentational bits ───────────────────────────────────────────────

export function SectionCard({ title, count, action, children }: {
  title: string; count?: number; action?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <Card style={{ padding: 0 }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--alga-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <Text size="sm" weight={600} style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }} tone="muted">
          {title}{typeof count === 'number' ? ` (${count})` : ''}
        </Text>
        {action}
      </div>
      <div style={{ padding: 16 }}>{children}</div>
    </Card>
  );
}

export function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, padding: '4px 0' }}>
      <Text size="sm" tone="muted" style={{ width: 150, flexShrink: 0 }}>{k}</Text>
      <div style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
        {typeof v === 'string' ? <Text size="sm">{v}</Text> : v}
      </div>
    </div>
  );
}

export function CopyButton({ value, label }: { value: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      variant="ghost"
      size="xs"
      title="Copy to clipboard"
      aria-label="Copy to clipboard"
      onClick={async () => {
        try { await navigator.clipboard.writeText(value); setDone(true); setTimeout(() => setDone(false), 1200); } catch { /* clipboard blocked */ }
      }}
    >{done ? '✓ copied' : (label ?? '⧉')}</Button>
  );
}

/** A one-time secret (install code, activation code, license key) shown large, monospace, with copy. */
export function SecretBlock({ label, value, multiline }: { label: string; value: string; multiline?: boolean }) {
  return (
    <Stack gap={6}>
      <Stack direction="row" gap={8} align="center" justify="space-between">
        <Text size="xs" tone="muted" weight={600} style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Text>
        <CopyButton value={value} label="Copy" />
      </Stack>
      <code style={{
        display: 'block', padding: '12px 14px', borderRadius: 8,
        border: '1px solid var(--alga-primary-border, #caa8f9)', background: 'var(--alga-primary-soft, #f6f0fe)',
        fontSize: multiline ? 12 : 20, letterSpacing: multiline ? 0 : '0.12em', fontWeight: 600,
        wordBreak: 'break-all', whiteSpace: multiline ? 'pre-wrap' : 'normal', maxHeight: multiline ? 160 : undefined, overflowY: multiline ? 'auto' : undefined,
      }}>{value}</code>
    </Stack>
  );
}

/** Label + control, stacked, for dialog forms. */
export function Field({ label, required, hint, children }: { label: string; required?: boolean; hint?: string; children: React.ReactNode }) {
  return (
    <Stack gap={4}>
      <Text size="sm" weight={600}>{label}{required ? <span style={{ color: 'var(--alga-danger)' }}> *</span> : null}</Text>
      {children}
      {hint && <Text size="xs" tone="muted">{hint}</Text>}
    </Stack>
  );
}

export function ExternalLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" style={{ color: 'var(--alga-primary, #8a4dea)', fontSize: 13 }}>{children} ↗</a>
  );
}
